import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  BottomSheet,
  CounterText,
  Divider,
  PageHeaderWrapper,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { Controller } from "react-hook-form";

import { TimeSlot } from "../../../Shift/Open/types";
import { IncludedStatesFilterButtonGroup } from "../IncludedStatesFilterButtonGroup";
import { QualificationsFilterButtonGroup } from "../QualificationsFilterButtonGroup";
import { ShiftTimeSlotButtonGroup } from "../ShiftTimeSlotButtonGroup";
import { DistanceFilter } from "./DistanceFilter";
import { FilterSectionSkeleton } from "./FilterSectionSkeleton";
import { FilterSectionWrapper } from "./FilterSectionWrapper";
import { FiltersBottomSheetFooter } from "./Footer";
import { ShowPriorityShiftsFilter } from "./ShowPriorityShiftsFilter";
import { useFilteredOpenShiftsCount } from "./useFilteredOpenShiftsCount";
import { useFilteredOpenShiftsStates } from "./useFilteredOpenShiftsStates";
import { useFiltersForm } from "./useFiltersForm";

interface ShiftDiscoveryFiltersBottomSheetProps {
  modalState: UseModalState;
}

export function ShiftDiscoveryFiltersBottomSheet(props: ShiftDiscoveryFiltersBottomSheetProps) {
  const { modalState } = props;

  const { data: workerQualifications, isSuccess: workerQualificationsAreLoaded } =
    useWorkerQualifications();

  const { form, changedFiltersCount, resetFilters, cancelFilters, submitFilters } =
    useFiltersForm();

  const formValues = form.watch();

  const { count: filteredOpenShiftsCount, isLoading: filteredOpenShiftsCountIsLoading } =
    useFilteredOpenShiftsCount(formValues);

  const includeWindowedOutShiftsEnabled = useCbhFlag(CbhFeatureFlag.INCLUDE_WINDOWED_OUT_SHIFTS, {
    defaultValue: false,
  });

  const {
    states: filteredOpenShiftsStates,
    hasShiftsInNonHomeStates,
    isLoading: filteredOpenShiftsStatesIsLoading,
  } = useFilteredOpenShiftsStates(formValues);
  const showIncludedStatesFilter =
    (isDefined(formValues.includedStates) && formValues.includedStates.length > 0) ||
    hasShiftsInNonHomeStates;

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <FiltersBottomSheetFooter
          showClearFormButton={changedFiltersCount > 0}
          openShiftsCount={filteredOpenShiftsCount}
          openShiftsIsLoading={filteredOpenShiftsCountIsLoading}
          onClear={() => {
            logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_CLEARED, {
              trigger: "Filters Modal",
              shiftCount: filteredOpenShiftsCount,
            });
            resetFilters();
          }}
          onCancel={() => {
            logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_CLOSED, {
              shiftCount: filteredOpenShiftsCount,
            });
            cancelFilters();
            modalState.closeModal();
          }}
          onSubmit={() => {
            logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_APPLIED, {
              trigger: "Filters Modal",
              shiftCount: filteredOpenShiftsCount,
              distance: formValues.distance,
              timeSlotsAll: formValues.shiftTimeSlots.length === 0,
              timeSlots:
                formValues.shiftTimeSlots.length === 0
                  ? [TimeSlot.AM, TimeSlot.PM, TimeSlot.NOC]
                  : formValues.shiftTimeSlots,
              licensesAll: formValues.licenses.length === 0,
              licenses:
                formValues.licenses.length === 0
                  ? workerQualifications?.map((q) => q.attributes.name)
                  : formValues.licenses,
              ...(includeWindowedOutShiftsEnabled && {
                includeWindowedOutShifts: formValues.includeWindowedOutShifts,
              }),
              includedStates: formValues.includedStates,
            });
            submitFilters();
            modalState.closeModal();
          }}
        />
      }
    >
      <DialogContent sx={{ paddingBottom: 12 }}>
        <Stack spacing={8}>
          <PageHeaderWrapper variant="tertiary">
            <CounterText count={changedFiltersCount} suffix="applied">
              <Title variant="h4" component="h4">
                Filters
              </Title>
            </CounterText>
          </PageHeaderWrapper>

          <Controller
            control={form.control}
            name="shiftTimeSlots"
            render={({ field }) => (
              <FilterSectionWrapper sectionTitle="Shift Times">
                <ShiftTimeSlotButtonGroup value={field.value} onChange={field.onChange} />
              </FilterSectionWrapper>
            )}
          />

          <Divider />

          <Controller
            control={form.control}
            name="distance"
            render={({ field }) => <DistanceFilter value={field.value} onChange={field.onChange} />}
          />

          <Divider />

          <Controller
            control={form.control}
            name="licenses"
            render={({ field }) => {
              return (
                <FilterSectionWrapper sectionTitle="Shift Roles">
                  {workerQualificationsAreLoaded ? (
                    <QualificationsFilterButtonGroup
                      value={field.value}
                      qualifications={workerQualifications ?? []}
                      onChange={field.onChange}
                    />
                  ) : (
                    <FilterSectionSkeleton ariaLabel="Shift Licenses Filter Loading" />
                  )}
                </FilterSectionWrapper>
              );
            }}
          />

          {includeWindowedOutShiftsEnabled && (
            <Controller
              control={form.control}
              name="includeWindowedOutShifts"
              render={({ field }) => (
                <>
                  <Divider />
                  <ShowPriorityShiftsFilter checked={field.value} onChange={field.onChange} />
                </>
              )}
            />
          )}

          {showIncludedStatesFilter && (
            <>
              <Divider />
              <Controller
                control={form.control}
                name="includedStates"
                render={({ field }) => (
                  <FilterSectionWrapper
                    sectionTitle="States"
                    sectionSubtitle="Show shifts in specific states"
                  >
                    {filteredOpenShiftsStatesIsLoading ? (
                      <FilterSectionSkeleton ariaLabel="States Filter Loading" />
                    ) : (
                      <IncludedStatesFilterButtonGroup
                        value={field.value}
                        availableStates={[...filteredOpenShiftsStates].sort()}
                        onChange={field.onChange}
                      />
                    )}
                  </FilterSectionWrapper>
                )}
              />
            </>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
